import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FOCUSRING } from '../focus';
import { formatGetal, isGetal, isTelling } from '../format';
import { BREEDTE_KLASSE, procentStap } from '../leerkaart';
import { isBruikbaarPercentage } from './MeasurementValue';
import { useMeldToestand, useVoorlees } from './Toestandsmelding';
import { heeftEigenNaam, PADREGEL, veldnaamVan } from '../veldnaam';
import { weeknummer } from '../week';
import type { Signalering, Signaleringenantwoord, Signaleringsbeoordelaar, Veldnamen } from '../work-quality.api';

export interface SignalListProps {
  antwoord: Signaleringenantwoord;
}

/**
 * Nakijkers (S5): per veld dat de dienst die week signaleert één Nakijkerkaart, in de volgorde van de
 * dienst (het paar dat het verst uiteenligt eerst). Eén lijst over Vastleggen en Controles samen
 * (besluit 31-10-2026 #1), dus nergens een werksoortnaam.
 *
 * De kaart gaat over een veld waarover twee nakijkers het oneens zijn, niet over een persoon: de naam
 * van de medewerker wiens werk het betrof staat er niet. Er is geen status en geen besprekingsknop
 * (EXPERIENCE.md afwijking 3); "in bespreking" volgt uit de meting. Elk getal komt letterlijk uit het
 * antwoord (AD-19).
 */

/** De twee vlakken zijn letterlijk gelijk — geen van beide nakijkers is de eerste of de grootste. */
const VLAK = 'flex flex-col gap-1';

/** Het paar dat deze kaart toont: het eerste, want de dienst zet het wijdste vooraan. */
function eerstePaar(signalering: Signalering): Signaleringsbeoordelaar[] {
  const paren = Array.isArray(signalering?.paren) ? signalering.paren : [];
  const beoordelaars = paren[0]?.beoordelaars;
  return Array.isArray(beoordelaars) ? beoordelaars.filter((rij) => rij !== null && typeof rij === 'object') : [];
}

function factorVan(signalering: Signalering): number | null {
  const factor = Array.isArray(signalering?.paren) ? signalering.paren[0]?.factor : undefined;
  return isGetal(factor) ? factor : null;
}

interface SignalCardProps {
  signalering: Signalering;
  veldnamen?: Veldnamen;
  week: string;
  minimum: number | null;
}

function SignalCard({ signalering, veldnamen, week, minimum }: SignalCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const voorlees = useVoorlees();
  const id = useId();
  const kopId = `${id}-kop`;
  const padId = `${id}-pad`;
  const [melding, setMelding] = useState<string | null>(null);

  const veld = signalering.veld;
  // De naam is wat de lezer ziet; `veld` blijft het pad, want dat draagt de testids en maakt elke
  // kaart uniek — spec-veldnamen, AC1.
  const veldlabel = veldnaamVan(veld, veldnamen);
  const eigenNaam = heeftEigenNaam(veld, veldnamen);
  // Namen zijn niet uniek ("Eenheid" hoort bij tientallen velden); de geplakte agendazin en de
  // beschrijving van de knop dragen daarom naam én pad.
  const veldEnPad = eigenNaam ? `${veldlabel} (${veld})` : veld;
  const beoordelaars = eerstePaar(signalering);
  const factor = factorVan(signalering);
  // Twee, en precies twee: bij één valt er niets naast te zetten, bij drie is er weer een rangorde.
  const volledig = beoordelaars.length === 2;

  function getal(waarde: unknown): string {
    return isTelling(waarde) ? formatGetal(waarde, locale) : t('qa.eigen.voorbeeldOnbekend');
  }

  /** Eén nakijker: naam als gewone tekst, de regel met percentage en aantallen, en de balk. */
  function nakijker(rij: Signaleringsbeoordelaar) {
    const aantallen = isTelling(rij.aantalProductenMetCorrectie) && isTelling(rij.aantalVergelekenProducten)
      ? { aantal: formatGetal(rij.aantalProductenMetCorrectie, locale), vergeleken: formatGetal(rij.aantalVergelekenProducten, locale) }
      : null;
    const procent = rij.percentageProductenMetCorrectie;

    // Een percentage buiten 0–100 krijgt geen balk en geen getal: de oude teamweergave kwam op 4292
    // uit en niemand zag het. Wat er dan staat zijn de onbewerkte aantallen, met de melding erbij.
    if (!isBruikbaarPercentage(procent)) {
      return { regel: aantallen === null ? t('qa.nakijkers.getallenOnbekend') : t('qa.nakijkers.percentageKloptNiet', aantallen), balk: null };
    }
    const percentage = formatGetal(procent, locale);
    return {
      regel: aantallen === null
        ? t('qa.nakijkers.gecorrigeerdZonderAantallen', { percentage })
        : t('qa.nakijkers.gecorrigeerd', { percentage, ...aantallen }),
      balk: {
        label: aantallen === null ? t('qa.nakijkers.balkZonderAantallen', { percentage }) : t('qa.nakijkers.balk', { percentage, ...aantallen }),
        // Een klein maar echt percentage krijgt minstens één stap, zodat het niet als 0% oogt.
        breedte: BREEDTE_KLASSE[procent > 0 ? Math.max(5, procentStap(procent)) : 0],
      },
    };
  }

  /**
   * Kopieert één zin naar het klembord en verstuurt niets. Lukt het niet — ook een browser zónder
   * klembord komt hier als TypeError terecht — dan zegt het Voorleesgebied dat, want een melding
   * "staat op je klembord" bij een lege plakactie stuurt iemand met lege handen het overleg in.
   */
  async function naarKlembord(): Promise<void> {
    const [a, b] = beoordelaars;
    const zin = t('qa.nakijkers.agendaTekst', {
      week,
      veld: veldEnPad,
      beoordelaarA: a?.naam ?? t('qa.eigen.voorbeeldOnbekend'),
      aantalA: getal(a?.aantalProductenMetCorrectie),
      vergelekenA: getal(a?.aantalVergelekenProducten),
      beoordelaarB: b?.naam ?? t('qa.eigen.voorbeeldOnbekend'),
      aantalB: getal(b?.aantalProductenMetCorrectie),
      vergelekenB: getal(b?.aantalVergelekenProducten),
    });
    let uitkomst: string;
    try {
      await navigator.clipboard.writeText(zin);
      uitkomst = t('qa.nakijkers.agendaGelukt', { veld: veldEnPad });
    } catch {
      uitkomst = t('qa.nakijkers.agendaMislukt');
    }
    // Het Voorleesgebied is het enige levende gebied; de zin naast de knop is voor wie kijkt.
    voorlees(uitkomst);
    setMelding(uitkomst);
  }

  return (
    <article
      className="flex min-w-0 flex-col gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-4"
      data-testid={`werkkwaliteit-signalering-${veld}`}
    >
      <div className="min-w-0">
        <h3
          className="break-words text-[0.9375rem] font-semibold text-[var(--foreground-heading)]"
          data-testid={`werkkwaliteit-signalering-kop-${veld}`}
          id={kopId}
        >
          {veldlabel}
        </h3>
        {eigenNaam ? (
          <p className={PADREGEL} id={padId} lang="en">
            {veld}
          </p>
        ) : null}
        <p className="mt-1 text-[0.8125rem] text-[var(--foreground)]">
          {factor === null ? t('qa.nakijkers.factorOnbekend') : t('qa.nakijkers.factor', { factor: formatGetal(factor, locale) })}
        </p>
      </div>

      {!volledig ? (
        <p className="text-[0.8125rem] text-[var(--foreground)]" data-testid={`werkkwaliteit-signalering-onvolledig-${veld}`}>
          {t('qa.nakijkers.onvolledig')}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {beoordelaars.map((rij, plek) => {
              const { regel, balk } = nakijker(rij);
              return (
                <div className={VLAK} data-testid={`werkkwaliteit-signalering-beoordelaar-${rij.validator}`} key={`${rij.validator}-${plek}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <span className="break-words text-[0.8125rem] font-medium text-[var(--foreground)]">{rij.naam}</span>
                    <span className="text-[0.8125rem] tabular-nums text-[var(--foreground)]">{regel}</span>
                  </div>
                  {balk !== null && (
                    <div aria-label={balk.label} className="h-2 w-full overflow-hidden rounded-full bg-[var(--border)]" role="img">
                      <div className={`h-full rounded-full bg-[var(--foreground-muted)] ${balk.breedte}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-soft)] pt-3">
            {minimum !== null && (
              <p className="text-[0.75rem] text-[var(--foreground-muted)]" data-testid={`werkkwaliteit-signalering-bijschrift-${veld}`}>
                {t('qa.nakijkers.bijschrift', { week, minimum: formatGetal(minimum, locale) })}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button
                aria-describedby={eigenNaam ? `${kopId} ${padId}` : kopId}
                className={`rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-[0.8125rem] font-medium text-[var(--foreground-heading)] hover:bg-[var(--background-muted)] max-md:min-h-[2.75rem] max-md:min-w-[2.75rem] ${FOCUSRING}`}
                data-testid={`werkkwaliteit-signalering-agenda-${veld}`}
                onClick={() => {
                  void naarKlembord();
                }}
                type="button"
              >
                {t('qa.nakijkers.agenda')}
              </button>
              {melding !== null && (
                <p className="text-[0.75rem] text-[var(--foreground-muted)]" data-testid={`werkkwaliteit-signalering-agenda-melding-${veld}`}>
                  {melding}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </article>
  );
}

export function SignalList({ antwoord }: SignalListProps) {
  return (
    <div className="flex min-w-0 flex-col gap-3" data-werkkwaliteit-sectie="signaleringen">
      <Inhoud antwoord={antwoord} />
    </div>
  );
}

/** Info-toestanden schrijven hun zin ook in het Voorleesgebied, zodat een weekwissel ze laat horen. */
function OudeMeetmethode() {
  const { t } = useTranslation();
  const zin = t('qa.eigen.weekOudeMeetmethode');
  useMeldToestand(zin);
  return (
    <p
      className="rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-5 text-[0.8125rem] text-[var(--foreground)]"
      data-testid="werkkwaliteit-nakijkers-oude-meetmethode"
      role="status"
    >
      {zin}
    </p>
  );
}

function GeenVerschillen({ week }: { week: string }) {
  const { t } = useTranslation();
  const kop = t('qa.nakijkers.geenKop', { week });
  const uitleg = t('qa.nakijkers.geenUitleg');
  useMeldToestand(`${kop} ${uitleg}`);
  return (
    <div
      className="rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-5 text-[0.8125rem] text-[var(--foreground)]"
      data-testid="werkkwaliteit-geen-signaleringen"
      role="status"
    >
      <h2 className="text-[0.9375rem] font-semibold text-[var(--foreground-heading)]" tabIndex={-1}>
        {kop}
      </h2>
      <p className="mt-1 text-[var(--foreground-muted)]">{uitleg}</p>
    </div>
  );
}

function Inhoud({ antwoord }: SignalListProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const afbakening = antwoord.afbakening;
  const week = weeknummer(afbakening?.weekKey ?? '');
  const minimum = isTelling(afbakening?.minimumBeoordeeldeProducten) ? afbakening.minimumBeoordeeldeProducten : null;
  const factor = isGetal(afbakening?.signaleringsfactor) ? afbakening.signaleringsfactor : null;
  const rijen = (Array.isArray(antwoord.signaleringen) ? antwoord.signaleringen : []).filter(
    (rij): rij is Signalering => rij !== null && typeof rij === 'object' && typeof rij.veld === 'string',
  );

  // "Er is niets gemeten" is iets anders dan "niemand liep uiteen": een gat in de meting mag nooit
  // als geruststelling lezen.
  if (antwoord.reden === 'niet_gemeten_in_deze_meetversie') return <OudeMeetmethode />;
  if (rijen.length === 0) return <GeenVerschillen week={week} />;

  return (
    <section aria-labelledby="werkkwaliteit-nakijkers-lijstkop" className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-col gap-1">
        <h2
          className="text-[0.9375rem] font-semibold text-[var(--foreground-heading)]"
          id="werkkwaliteit-nakijkers-lijstkop"
          tabIndex={-1}
        >
          {t('qa.nakijkers.lijstKop', { count: rijen.length })}
        </h2>
        {minimum !== null && factor !== null && (
          <p className="text-[0.75rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-nakijkers-regel">
            {t('qa.nakijkers.regel', { minimum: formatGetal(minimum, locale), factor: formatGetal(factor, locale) })}
          </p>
        )}
      </div>
      <div className="grid min-w-0 gap-3 xl:grid-cols-2">
        {rijen.map((signalering, plek) => (
          <SignalCard
            // De week hoort in de sleutel: een andere week met hetzelfde veld begint zonder oude klembordmelding.
            key={`${afbakening?.weekKey}-${signalering.veld}-${plek}`}
            minimum={minimum}
            signalering={signalering}
            veldnamen={antwoord.veldnamen}
            week={week}
          />
        ))}
      </div>
    </section>
  );
}
